import { useCallback, useEffect, useState } from "react";
import { useAlert, useSelectOptions } from "hooks";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

/**
 * Fetch data from API to fill dropdown options for autocomplete
 * @param queryName unique field name
 * @param fetchFn fetch function
 * @param text search value
 * @param minLength min length to
 */
export const useAutocompleteFetch = (
    queryName: string,
    fetchFn: (queryName: string, searchValue: string) => Promise<string[]>,
    text: string,
    minLength = 1
) => {
    const { t } = useTranslation();
    const { toggleAlert } = useAlert();
    const { isFetching, isError, data: result } = useQuery([queryName, text], fetchFn, {
        enabled: text.length >= minLength,
    });
    const [autocompleteOptions, setAutocompleteOptions] = useState<string[]>([]);
    const options = useSelectOptions(autocompleteOptions);

    useEffect(() => {
        if (!isFetching && result) {
            return setAutocompleteOptions(result.map((value) => value));
        }

        if (isError) {
            toggleAlert({
                type: "error",
                message: t("general.errors.autocompleteError"),
            });
        }
    }, [text, toggleAlert, minLength, result, isFetching, isError, t]);

    const clearOptions = useCallback(() => setAutocompleteOptions([]), []);

    return {
        isLoading: isFetching,
        options,
        clearOptions,
    };
};
